import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileCode, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { getAllDescriptionActions } from "reedy-data";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { Button } from "~/components/button";
import { Text } from "~/components/text";
import { BetaBadge } from "~/components/beta-badge";
import {
  getActionsByType,
  IProduct,
} from "~/integrations/firebase/collections";
import { DescriptionType } from "~/api/client/products";
import { actionInProgress } from "~/utils/status";
import { GENERATE_METADATA_BOOKS_LIMIT } from "~/shared/constants";
import { saveProductsAsExcel } from "~/shared/services/export-products-service";
import { useAnalytics } from "~/contexts/analytics-context";
import { useFeatureFlags } from "~/contexts/feature-flag-context";
import {
  BulkGenerateDialog,
  BulkGenerateDialogs,
} from "./bulk-generate-dialogs";
import { BulkGenerateOption } from "./bulk-generate-option";
import { GenerateAPlusContentOption } from "./generate-a-plus-content-option";
import { ExportOption } from "~/pages/project-details/table/export-option";
import { OnixExportDialogs } from "./onix-export-dialogs";

const isGenerationInProgress = (product: IProduct) => {
  const { keywords, thema } = getActionsByType(product);
  const descriptionGenerateActions = Object.values(
    getAllDescriptionActions(product) ?? {},
  )
    .map((action) => action?.generate)
    .filter((action) => action !== undefined);

  return actionInProgress(
    ...descriptionGenerateActions,
    keywords?.generate,
    thema?.generate,
  );
};

interface ProjectTakeActionDropdownProps {
  products: IProduct[];
  projectName: string;
  generateKeywords: (products: IProduct[]) => void;
  generateDescriptions: (
    products: IProduct[],
    descriptionTypes: DescriptionType[],
  ) => void;
  generateThemaCodes: (products: IProduct[]) => void;
}

export const ProjectTakeActionDropdown = ({
  products,
  projectName,
  generateKeywords,
  generateDescriptions,
  generateThemaCodes,
}: ProjectTakeActionDropdownProps) => {
  const { t } = useTranslation(["projectDetails"]);
  const { gaEvent } = useAnalytics();
  const { features } = useFeatureFlags();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openGenerateDialog, setOpenGenerateDialog] =
    useState<BulkGenerateDialog | null>(null);
  const [showOnixWarning, setShowOnixWarning] = useState(false);

  const noBooksSelected = products.length === 0;
  const isOverGenerateLimit = products.length > GENERATE_METADATA_BOOKS_LIMIT;
  const isGenerating = products.some(isGenerationInProgress);
  const generateDisabled =
    isOverGenerateLimit ||
    isGenerating ||
    products.some((product) => product.nonExistent);

  const getGenerateTooltip = () => {
    if (isOverGenerateLimit) {
      return t("projectDetails:buttons.generate.disabledLimitBooks", {
        count: GENERATE_METADATA_BOOKS_LIMIT,
      });
    }
    if (isGenerating) {
      return t("projectDetails:buttons.generateAll.tooltips.generating");
    }
    return undefined;
  };
  const generateTooltip = getGenerateTooltip();

  const handleOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (!open) return;
    gaEvent({
      type: "project_take_action_opened",
      payload: { books_count: products.length },
    });
  };

  const handleExcelExport = async () => {
    try {
      gaEvent({
        type: "export_books",
        payload: {
          books_count: products.length,
          export_type: "excel",
        },
      });
      await saveProductsAsExcel(products, projectName);
    } catch {
      toast.error(t("projectDetails:buttons.export.error"));
    }
  };

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={handleOpenChange}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="primary"
                  disabled={noBooksSelected}
                  label={t("projectDetails:buttons.takeAction.label")}
                  icon="chevron-down"
                  iconPosition="right"
                  className="w-full h-10"
                />
              </DropdownMenuTrigger>
            </span>
          </TooltipTrigger>

          {noBooksSelected ? (
            <TooltipContent
              role="tooltip"
              side="bottom"
              className="font-light text-center shadow-tooltip text-secondary-600 whitespace-pre px-5 z-10 border-none"
              sideOffset={6}
            >
              <TooltipArrow
                className="fill-white drop-shadow-xs"
                width={12}
                height={6}
              />
              {t("projectDetails:buttons.takeAction.disabledNoBooks")}
            </TooltipContent>
          ) : null}
        </Tooltip>

        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuLabel>
            <Text as="span" variant="captionStrong">
              {t("projectDetails:buttons.generateAll.title")}
            </Text>
          </DropdownMenuLabel>
          <BulkGenerateOption
            disabled={generateDisabled}
            tooltip={generateTooltip}
            onClick={() => setOpenGenerateDialog("keywords")}
            label={t("projectDetails:buttons.generateAll.items.keywords")}
          />
          <BulkGenerateOption
            disabled={generateDisabled}
            tooltip={generateTooltip}
            onClick={() => setOpenGenerateDialog("descriptions")}
            label={t("projectDetails:buttons.generateAll.items.descriptions")}
          />
          <BulkGenerateOption
            disabled={generateDisabled}
            tooltip={generateTooltip}
            onClick={() => setOpenGenerateDialog("themaCodes")}
            label={t("projectDetails:buttons.generateAll.items.themaCodes")}
          />

          <DropdownMenuSeparator />

          {features.amplifyProjects ? (
            <>
              <DropdownMenuLabel>
                <Text as="span" variant="captionStrong">
                  {t("projectDetails:buttons.amplify.title")}
                </Text>
              </DropdownMenuLabel>
              <GenerateAPlusContentOption
                products={products}
                onDone={() => setIsMenuOpen(false)}
              />

              <DropdownMenuSeparator />
            </>
          ) : null}

          <DropdownMenuLabel>
            <Text as="span" variant="captionStrong">
              {t("projectDetails:buttons.export.title")}
            </Text>
          </DropdownMenuLabel>
          <ExportOption
            onClick={handleExcelExport}
            icon={<FileSpreadsheet className="w-5 h-5 mr-2" />}
            label={t("projectDetails:buttons.export.items.excel")}
          />
          <ExportOption
            onClick={() => setShowOnixWarning(true)}
            icon={<FileCode className="w-5 h-5 mr-2" />}
            label={t("projectDetails:buttons.export.items.onix")}
            badge={<BetaBadge />}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      <BulkGenerateDialogs
        products={products}
        openDialog={openGenerateDialog}
        onOpenDialogChange={setOpenGenerateDialog}
        generateKeywords={generateKeywords}
        generateDescriptions={generateDescriptions}
        generateThemaCodes={generateThemaCodes}
      />

      <OnixExportDialogs
        products={products}
        showWarning={showOnixWarning}
        onShowWarningChange={setShowOnixWarning}
      />
    </>
  );
};
